import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import Modal from '../../../../../components/Modal';
import Button from '../../../../../components/Button';
import TableBox from '../../../../../components/TableBox';
import Accordian from '../../../../../components/Accordian';
import { PERMISSION_MATRIX } from '../../../../../utils/appConstant';
import { TableContextProvider } from '../../../../../interfaces/common';
import { SortOrder } from '../../../../../enums';
import PermissionMetricsRow from './PermissionMetricsRow';

interface PermissionMetricsModalProps {
  open: boolean;
  onClose: () => void;
}

interface ModuleData {
  moduleName: string;
  rows: any[];
  actions: string[];
}

const PermissionMetricsModal: React.FC<PermissionMetricsModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();

  const [expandedModules, setExpandedModules] = useState<{ [key: string]: boolean }>({});

  const modulesData = useMemo(() => {
    const modules: ModuleData[] = [];
    
    PERMISSION_MATRIX.forEach((moduleData) => {
      const moduleName = moduleData.Module;
      const roles = moduleData.Roles;
      const moduleActions = new Set<string>();
      const rows: any[] = [];
      
      Object.keys(roles).forEach((roleName) => {
        const roleData = (roles as any)[roleName];
        Object.keys(roleData).forEach((action) => {
          const value = roleData[action];
          if (value !== '' && value !== null && value !== undefined) {
            moduleActions.add(action);
          }
        });
      });
      
      const actionsArray = Array.from(moduleActions);
      
      Object.keys(roles).forEach((roleName) => {
        const roleData = (roles as any)[roleName];
        const rowData: any = {
          role: roleName,
        };
        
        actionsArray.forEach((action) => {
          rowData[action] = roleData[action] ?? '';
        });
        
        rows.push(rowData);
      });
      
      modules.push({
        moduleName,
        rows,
        actions: actionsArray,
      });
    });
    
    return modules;
  }, []);

  const getHeadCells = useCallback((actions: string[]) => {
    const cells = [
      { id: 'role', label: 'Role', sortEnabled: false, width: '25%' },
    ];
    
    actions.forEach((action) => {
      cells.push({
        id: action,
        label: action,
        sortEnabled: false,
        width: `${75 / actions.length}%`,
      });
    });
    
    return cells;
  }, []);

  const getTableValues = useCallback((moduleData: ModuleData): TableContextProvider => {
    const headCells = getHeadCells(moduleData.actions);
    
    return {
      row: PermissionMetricsRow,
      data: moduleData.rows,
      pagination: false,
      headCells,
      order: SortOrder.asc,
      orderBy: '',
      page: 1,
      perPage: moduleData.rows.length,
      total: moduleData.rows.length,
      loading: false,
      noDataFoundMessage: t('common.table.noRecordsFound'),
      setPage: () => {},
      setPerPage: () => {},
      setOrder: () => {},
      setOrderBy: () => {},
      rowProps: { actions: moduleData.actions },
    };
  }, [getHeadCells, t]);

  const handleAccordionChange = useCallback((moduleName: string) => {
    setExpandedModules((prev) => ({
      ...prev,
      [moduleName]: !prev[moduleName],
    }));
  }, []);

  const modalAction = (
    <Button
      className="btn btn-outline-secondary"
      text={t('common.errorMessages.unHandledRejectionPage.closeBtn')}
      onClick={onClose}
    />
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('users.addEdit.pageTexts.permissionMatrix.title')}
      titleClassName="text-start"
      content={
        <div>
          {modulesData.map((moduleData) => {
            const isExpanded = expandedModules[moduleData.moduleName] ?? false;
            const summary = (
              <>
                <h6 className="mb-0">{moduleData.moduleName}</h6>
              </>
            );
            
            const content = (
              <TableBox tableValues={getTableValues(moduleData)} />
            );

            return (
              <Accordian
                key={moduleData.moduleName}
                expanded={isExpanded}
                summary={summary}
                content={content}
                bodyClassName="pb-0 px-0"
                onChange={() => handleAccordionChange(moduleData.moduleName)}
              />
            );
          })}
        </div>
      }
      action={modalAction}
      actionClassName="justify-content-start"
      maxWidth="xl"
    />
  );
};

export default PermissionMetricsModal;

